import { useCallback, useRef } from 'react';

/**
 * The in-flight lock of a dialog, in one place (#1060 review 1, finding 6).
 * While `busy` (a request the dialog sent has not answered) every way out
 * holds: `dismiss` does nothing, and the close button `closeProps` spreads
 * is disabled. Route Escape, the backdrop and any Done-style close through
 * `dismiss`; spread `closeProps` on the X. Once the request answers, both
 * close again, success or failure.
 *
 * `dismiss` is stable and reads the latest `busy` and `onClose`, so a
 * handler captured before the request started (a keydown listener bound on
 * open) still refuses while it runs.
 *
 * The control that sent the request shows it (`<Button loading>`, which
 * keeps focus); the dialog's other actions refuse with the same flag. The
 * group Empathy sheets (#1055) keep their own copy for now
 * (docs/REVIEW-NITS-BACKLOG.md).
 */
export function useDialogLock(
  busy: boolean,
  onClose: () => void,
): {
  dismiss: () => void;
  closeProps: { disabled: boolean; onClick: () => void };
} {
  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };
  const dismiss = useCallback(() => {
    if (latest.current.busy) return;
    latest.current.onClose();
  }, []);
  // The X's own gate is `disabled` (a disabled button fires no click), so
  // its handler is the plain close: one gate, which a test can see fail.
  return { dismiss, closeProps: { disabled: busy, onClick: onClose } };
}
